"use client";

import { Checkbox } from "@arcadeai/design-system/components/ui/atoms/checkbox";
import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import {
  MobileTooltip,
  MobileTooltipContent,
  MobileTooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/mobile-tooltip";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import { Package } from "lucide-react";
import { useId } from "react";
import type { ToolkitSelectionMap } from "../hooks/use-toolkit-picker";
import type { SelectionMode } from "../index";

export type ToolkitSelectionSummaryProps = {
  selectedToolkits: ToolkitSelectionMap;
  selectedTools: Set<string>;
  onClear: () => void;
  onSelectAll?: () => void;
  allVisibleSelected?: boolean;
  selectionMode?: SelectionMode;
};
const maxVisibleIcons = 3;

export function ToolkitSelectionSummary({
  selectedToolkits,
  selectedTools,
  onClear,
  onSelectAll,
  allVisibleSelected,
  selectionMode,
}: ToolkitSelectionSummaryProps) {
  const checkboxId = useId();
  const hasSelections = selectedToolkits.size > 0 || selectedTools.size > 0;
  const selectedToolkitNames = Array.from(selectedToolkits.keys());
  const toolsCount = selectedTools.size;
  const toolkitsCount = selectedToolkitNames.length;

  const visibleNames = selectedToolkitNames.slice(0, maxVisibleIcons);
  const overflow = Math.max(toolkitsCount - visibleNames.length, 0);

  const handleCheckedChange = (checked: boolean) => {
    if (checked) {
      onSelectAll?.();
    } else {
      onClear();
    }
  };

  return (
    <div
      className="flex items-center gap-3"
      data-testid="toolkit-selection-summary"
    >
      {!hasSelections && (
        <div className="flex items-center gap-2 text-neutral-500 text-sm dark:text-neutral-400">
          <Package className="h-4 w-4" />
          <span>No items selected</span>
        </div>
      )}

      {!!hasSelections && (
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <MobileTooltip>
            <MobileTooltipTrigger
              render={
                <div className="grid min-h-8 w-full grid-cols-1 items-center gap-x-3 gap-y-1 rounded-lg bg-neutral-100 px-2 py-1.5 text-xs sm:min-h-10 sm:w-auto sm:grid-cols-[auto_auto] sm:px-3 sm:py-2 sm:text-sm dark:bg-neutral-800" />
              }
            >
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-neutral-600 sm:h-4 sm:w-4 dark:text-neutral-300" />
                <span className="font-medium text-neutral-900 dark:text-neutral-100">
                  {`${toolsCount} tool${toolsCount === 1 ? "" : "s"} selected`}
                </span>
                <div className="ml-1 flex items-center sm:ml-1.5">
                  {visibleNames.map((name: string, idx: number) => {
                    const toolkit = selectedToolkits.get(name);
                    const Icon = getToolkitIcon(toolkit?.metadata?.id);
                    return (
                      <div
                        className={`relative ${idx > 0 ? "-ml-2" : ""}`}
                        key={`mini-icon-${name}`}
                        style={{ zIndex: idx + 1 }}
                      >
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-neutral-200 p-0.5 ring-1 ring-black/15 sm:h-6 sm:w-6 dark:bg-neutral-700 dark:ring-white/15">
                          <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </div>
                      </div>
                    );
                  })}
                  {overflow > 0 && (
                    <span
                      className="-ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-neutral-200 font-medium text-[10px] text-neutral-700 ring-1 ring-black/15 sm:h-6 sm:w-6 dark:bg-neutral-700 dark:text-neutral-200 dark:ring-white/15"
                      style={{ zIndex: visibleNames.length + 10 }}
                    >
                      +{overflow}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-neutral-600 text-xs dark:text-neutral-400">
                {`from ${toolkitsCount} MCP server${toolkitsCount === 1 ? "" : "s"}`}
              </div>
            </MobileTooltipTrigger>
            <MobileTooltipContent side="top" sideOffset={6}>
              <div className="max-w-64 text-wrap text-xs">
                {selectedToolkitNames.join(", ")}
              </div>
            </MobileTooltipContent>
          </MobileTooltip>
        </div>
      )}

      {/* Mobile actions row */}
      {selectionMode !== "single" && (
        <div className="ml-auto flex items-center gap-1.5 sm:hidden">
          <Checkbox
            checked={allVisibleSelected}
            id={checkboxId}
            indeterminate={hasSelections && !allVisibleSelected}
            onCheckedChange={handleCheckedChange}
          />
          <Label className="cursor-pointer text-sm" htmlFor={checkboxId}>
            Select all
          </Label>
        </div>
      )}
    </div>
  );
}
